import { use } from 'i18next';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';

interface UseDialogStorageActionsProps {
    caseId: string;
    selectedByPlatform: any;
    clickedAccountId: string | null;
}

const EXPIRY_TIME = 120000; // 2 minutes (120,000 ms)

// Manages the disabled search button state based on local storage && selected accounts ids state
const useDialogStorageActions = ({ caseId, selectedByPlatform, clickedAccountId }: UseDialogStorageActionsProps) => {
    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    useEffect(() => {
        let shouldDisable = false;
        const newSelectedIds: string[] = [];

        if (clickedAccountId && isIdInLocalStorage(clickedAccountId)) {
            shouldDisable = true;
        }

        _.forOwn(selectedByPlatform, (accounts) => {
            accounts.forEach((account) => {
                if (!clickedAccountId && isIdInLocalStorage(account.id)) {
                    shouldDisable = true;
                }
                newSelectedIds.push(account.id);
            });
        });

        setIsButtonDisabled(shouldDisable);
        setSelectedIds(newSelectedIds);
    }, [selectedByPlatform, clickedAccountId]);

    useEffect(() => {
        return () => {
            clearExpiredIds();
        };
    }, []);

    const clearExpiredIds = useCallback(() => {
        const itemStr = localStorage.getItem(caseId);

        if (!itemStr) {
            return;
        }

        const item = JSON.parse(itemStr);
        const now = new Date();

        if (now.getTime() > item.expiry) {
            localStorage.removeItem(caseId);
        }
    }, []);

    // Retrieves account IDs being added from local storage.
    // If the item is expired, it removes it from local storage
    const getItemWithExpiry = useCallback(() => {
        const itemStr = localStorage.getItem(caseId);

        if (!itemStr) {
            return null;
        }

        const item = JSON.parse(itemStr);
        const now = new Date();

        if (now.getTime() > item.expiry) {
            localStorage.removeItem(caseId);
            return null;
        }

        return item.value;
    }, []);

    // Adds an item to local storage with an expiry time
    const addItemWithExpiry = useCallback(
        (value: string | string[]) => {
            let newValue = typeof value === 'string' ? [value] : value;

            const existingItem = getItemWithExpiry();
            if (existingItem) {
                newValue = Array.from(new Set([...existingItem, ...newValue]));
            }

            const item = {
                value: newValue,
                expiry: new Date().getTime() + EXPIRY_TIME,
            };

            setIsButtonDisabled(true);
            localStorage.setItem(caseId, JSON.stringify(item));
        },
        [getItemWithExpiry, setIsButtonDisabled],
    );

    // Checks if an id is in local storage, and sets the button state accordingly
    const isIdInLocalStorage = useCallback(
        (id: string | string[]) => {
            let result = false;
            const item = getItemWithExpiry();

            if (!item) {
                return false;
            }

            if (typeof id === 'string') {
                result = item.includes(id);
            }
            if (Array.isArray(id)) {
                result = id.some((i) => item.includes(i));
            }

            setIsButtonDisabled(result);
            return result;
        },
        [getItemWithExpiry, setIsButtonDisabled],
    );

    return {
        selectedIds,
        addItemWithExpiry,
        isButtonDisabled,
    };
};

export default useDialogStorageActions;
